'use client'

import { useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { sendUAConversion } from '@/lib/measurement/ua-client'

type PurchaseLookupResponse = {
  purchase: {
    price: number
    currency: 'USD'
    order_id: string
    description: string
    content_id?: string
    item_quantity?: number
    user_id?: string | null
    user_name?: string | null
    custom_event_2?: boolean
  }
}

const FIRED_KEY_PREFIX = '__atrium_ua_purchase_fired:'

function isPurchaseSuccess(pathname: string, searchParams: URLSearchParams): boolean {
  if (pathname === '/budget') {
    return searchParams.get('deposit') === 'success' || searchParams.get('success') === 'true'
  }
  if (pathname === '/usage') {
    return searchParams.get('purchase') === 'success'
  }
  if (/^\/plans\/[^/]+\/controls$/.test(pathname)) {
    return searchParams.get('funding') === 'success'
  }
  if (/^\/locations\/[^/]+\/thank-you$/.test(pathname)) {
    return true
  }
  return false
}

export function UAPurchaseTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!isPurchaseSuccess(pathname, searchParams)) return

    const sessionId = searchParams.get('session_id')
    if (!sessionId) return

    const firedKey = `${FIRED_KEY_PREFIX}${sessionId}`
    try {
      if (sessionStorage.getItem(firedKey)) return
    } catch {}

    let cancelled = false
    const lookupAndFire = async () => {
      const response = await fetch(`/api/measurement/ua-purchase?session_id=${encodeURIComponent(sessionId)}`)
      if (!response.ok || cancelled) return
      const data = (await response.json()) as PurchaseLookupResponse
      const purchase = data.purchase
      sendUAConversion('purchase', purchase)
      if (purchase.custom_event_2) {
        sendUAConversion('custom_event_2', {
          ...purchase,
          description: 'First wallet funded over threshold',
        })
      }
      try {
        sessionStorage.setItem(firedKey, '1')
      } catch {}
    }

    void lookupAndFire()
    return () => {
      cancelled = true
    }
  }, [pathname, searchParams])

  return null
}
